import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function General() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);

  const [data, setData] = useState([]);
 const [sortConfig, setSortConfig] = useState({
  key: "id",
  direction: "asc",
});

  const [currentPage, setCurrentPage] = useState(1);
 const [searchTerm, setSearchTerm] = useState(() => {
  return localStorage.getItem("globalSearch") || "";
});
  const rowsPerPage = 8;

useEffect(() => {
  const storedSearch = localStorage.getItem("globalSearch");
  if (storedSearch) {
    setSearchTerm(storedSearch);
    localStorage.removeItem("globalSearch");
  }
}, []);

  /* =======================
     FETCH DATA (ONLY ONE API)
  ======================= */
 useEffect(() => {
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  if (!isLoggedIn) {
    navigate("/", { replace: true });
    return;
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch("/report/user/compliance", {
        method: "GET",
      });

      const records = res?.data || [];

      const formatted = records.map((row) => ({
        id: row.report_id,
        act: row.act,
        instance: `${row.name || ""} - ${row.title || ""}`,
        description: row.description || "",
        startDate: row.start_date,
        actionDate: row.action_date,
        endDate: row.end_date,
        status: row.status,
        requestDate: row.request_date,
        responseDate: row.response_date,
      }));

      setData(formatted);
    } catch (err) {
      console.error("Error fetching general report:", err);
      setError("Failed to fetch records.");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [navigate]);

  /* =======================
     SEARCH
  ======================= */
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(
      (item) =>
        item.instance?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.act?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);
  const exportToCSV = () => {
  const headers = [
    "Id",
    "Compliance",
    "Instance",
    "Description",
    "Start Date",
    "Action Date",
    "End Date",
    "Status",
    "Request Date",
    "Response Date",
  ];

  const rows = data.map((r) => [
    r.id,
    r.act,
    r.instance,
    r.description,
    r.startDate,
    r.actionDate,
    r.endDate,
    r.status,
    r.requestDate,
    r.responseDate,
  ]);

  const csv =
    [headers, ...rows]
      .map((e) => e.map((v) => `"${v ?? ""}"`).join(","))
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "general_report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  /* =======================
     SORT
  ======================= */
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = (a[sortConfig.key] || "").toString().toLowerCase();
        const valB = (b[sortConfig.key] || "").toString().toLowerCase();
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

   const statusCounts = useMemo(() => {
    const counts = {
      pending: 0,
      requested: 0,
      approved: 0,
      total: 0,
    };
  
    filteredData.forEach((item) => {
      counts.total++;
  
      const status = item.status?.toLowerCase();
      if (status === "pending") counts.pending++;
      else if (status === "requested") counts.requested++;
      else if (status === "approved") counts.approved++;
    });
  
    return counts;
  }, [filteredData]);

  /* =======================
     PAGINATION
  ======================= */
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  /* =======================
     UI
  ======================= */
  return (
    <div className="general_">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Comprehensive Report</div>
      </div>

      <div
  className="table-header"
  style={{ display: "flex", justifyContent: "space-between" }}
>
  <div style={{ marginLeft: "45px" }}></div>

  <div className="table-actions" style={{ display: "flex", gap: "10px" }}>
    <div style={{ display: "flex", gap: "10px" }}>
    
    <div style={{
  background: "#e2e3e5",
  color: "#383d41",
  padding: "6px 12px",
  borderRadius: "20px",
  fontWeight: "600",
  fontSize: "13px"
}}>
  Total: {statusCounts.total}
</div>
    <div style={{
      background: "#f8d7da",
      color: "#721c24",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Pending: {statusCounts.pending}
    </div>

    <div style={{
      background: "#fff3cd",
      color: "#856404",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Requested: {statusCounts.requested}
    </div>

    <div style={{
      background: "#d4edda",
      color: "#155724",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Approved: {statusCounts.approved}
    </div>

  </div>
    <div className="search-box">
      <input
        placeholder="Search by Act or Instance"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <FaSearch className="search-icon" />
    </div>

    <button className="action-btn primary" onClick={exportToCSV}>
      Export
    </button>
  </div>
</div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("id")}>Id {getSortIcon("id")}</th>
            <th onClick={() => requestSort("act")}>Compliance {getSortIcon("act")}</th>
            <th>Instance</th>
            <th onClick={() => requestSort("startDate")}>Start Date</th>
            <th onClick={() => requestSort("actionDate")}>Action Date</th>
            <th onClick={() => requestSort("endDate")}>End Date</th>
            <th onClick={() => requestSort("status")}>Status</th>
            <th onClick={() => requestSort("requestDate")}>Request Date</th>
            <th onClick={() => requestSort("responseDate")}>Response Date</th>
          </tr>
        </thead>

       <tbody>
  {loading ? (
    <tr>
      <td colSpan="9" style={{ textAlign: "center" }}>
        Loading...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan="9" style={{ textAlign: "center", color: "red" }}>
        {error}
      </td>
    </tr>
  ) : currentRows.length === 0 ? (
    <tr>
      <td colSpan="9" style={{ textAlign: "center" }}>
        No records found
      </td>
    </tr>
  ) : (
    currentRows.map((row, index) => (
      <tr key={index}>
        <td>{row.id}</td>
        <td>{row.act}</td>

        {/* INSTANCE + TOOLTIP */}
       <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    
    <span>{row.instance}</span>

    {row.description && (
      <IoIosInformationCircle
        title={row.description}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}

  </div>
</td>

        <td>{row.startDate}</td>
        <td>{row.actionDate}</td>
        <td>{row.endDate}</td>

        <td
          style={{
            color:
              row.status?.toLowerCase() === "approved"
                ? "green"
                : row.status?.toLowerCase() === "requested"
                ? "#f0ad4e"
                : "red",
            fontWeight: 600,
          }}
        >
          {row.status}
        </td>

        <td>{row.requestDate}</td>
        <td>{row.responseDate}</td>
      </tr>
    ))
  )}
</tbody>
      </table>

      <div className="pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
          Prev
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
